import { supabase } from '../supabaseClient';
import { getPlanName, PAYMENT_CATALOG } from '../config/paymentCatalog';
import { getExpectedPaymentEnvironment, MercadoPagoService } from './MercadoPagoService';

/**
 * Serviço para gerenciamento de planos e assinaturas
 */
export class PlanService {
  
  /**
   * Busca todos os planos disponíveis
   */
  static async getPlanos() {
    try {
      const { data, error } = await supabase
        .from('planos_config')
        .select('*')
        .eq('ativo', true)
        .order('ordem_exibicao', { ascending: true });
      
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('Erro ao buscar planos:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Busca o plano de um usuário específico
   */
  static async getPlanUsuario(userId) {
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('plano, plano_data_inicio, plano_data_expiracao, plano_periodicidade, plano_trial, plano_trial_usado')
        .eq('id', userId)
        .single();
      
      if (error) throw error;

      // Busca configuração do plano
      const { data: config, error: configError } = await supabase
        .from('planos_config')
        .select('*')
        .eq('plano', data.plano)
        .single();
      
      if (configError) throw configError;

      return { 
        success: true, 
        data: {
          ...data,
          config,
          isExpirado: data.plano_data_expiracao ? new Date(data.plano_data_expiracao) < new Date() : false
        }
      };
    } catch (err) {
      console.error('Erro ao buscar plano do usuário:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Verifica se usuário pode usar um recurso
   */
  static async verificarLimite(userId, recurso) {
    try {
      const { data, error } = await supabase
        .rpc('verificar_limite_usuario', {
          p_user_id: userId,
          p_recurso: recurso
        });
      
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('Erro ao verificar limite:', err);
      return { success: false, error: err.message, data: { permitido: false } };
    }
  }

  /**
   * Verifica múltiplos recursos de uma vez
   */
  static async verificarMultiplosLimites(userId, recursos = []) {
    try {
      const resultados = {};
      
      for (const recurso of recursos) {
        const resultado = await this.verificarLimite(userId, recurso);
        resultados[recurso] = resultado.data;
      }
      
      return { success: true, data: resultados };
    } catch (err) {
      console.error('Erro ao verificar múltiplos limites:', err);
      return { success: false, error: err.message };
    }
  }

  /** Inicia o Starter Trial de 30 dias por uma função protegida no banco. */
  static async iniciarTrial(userId) {
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || authData.user?.id !== userId) throw new Error('Usuário inválido.');
      const { data, error } = await supabase.rpc('iniciar_trial_climorax');
      if (error) throw error;
      return {
        success: true,
        data: {
          dataExpiracao: data?.expira_em,
          diasRestantes: data?.dias_restantes || 30,
        }
      };
    } catch (err) {
      console.error('❌ Erro ao iniciar trial:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Finaliza trial e volta para plano gratuito
   */
  static async finalizarTrial(userId) {
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || authData.user?.id !== userId) throw new Error('Usuário inválido.');
      const { data, error } = await supabase.rpc('finalizar_trial_climorax');
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('❌ Erro ao finalizar trial:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Cancela assinatura (downgrade para gratuito)
   */
  static async cancelarAssinatura(userId, motivo = 'cancelamento') {
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || authData.user?.id !== userId) {
        throw new Error('Não é possível cancelar o plano de outro usuário.');
      }

      const subscriptions = await this.getAssinaturasMercadoPago(userId);
      if (!subscriptions.success) throw new Error(subscriptions.error || 'Não foi possível localizar a assinatura.');
      const current = subscriptions.data.assinaturas.find(item =>
        ['premium', 'pro', 'essential', 'professional', 'management'].includes(item.produto)
          && ['pending', 'authorized', 'paused'].includes(item.status)
      );
      if (!current) throw new Error('Nenhuma assinatura recorrente ativa foi encontrada.');
      const data = await MercadoPagoService.gerenciarAssinatura(current.preapproval_id, 'cancelar');
      return { success: true, data: { ...data, motivo } };
    } catch (err) {
      console.error('❌ Erro ao cancelar assinatura:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Busca histórico de planos do usuário
   */
  static async getHistorico(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('historico_planos')
        .select('*')
        .eq('user_id', userId)
        .order('data_mudanca', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('Erro ao buscar histórico:', err);
      return { success: false, error: err.message };
    }
  }

  static async getAssinaturasMercadoPago(userId) {
    try {
      const [subscriptionsResult, chargesResult] = await Promise.all([
        supabase
          .from('assinaturas_mercadopago')
          .select('preapproval_id,user_id,produto,periodicidade,status,valor,ambiente,proxima_cobranca,acesso_ate,criado_em,atualizado_em')
          .eq('user_id', userId)
          .eq('ambiente', getExpectedPaymentEnvironment())
          .order('atualizado_em', { ascending: false }),
        supabase
          .from('cobrancas_assinatura_mercadopago')
          .select('authorized_payment_id,preapproval_id,payment_id,status,valor,cobrado_em,criado_em')
          .order('criado_em', { ascending: false })
          .limit(20)
      ]);

      if (subscriptionsResult.error) throw subscriptionsResult.error;
      if (chargesResult.error) throw chargesResult.error;
      const subscriptionIds = new Set((subscriptionsResult.data || []).map(item => item.preapproval_id));
      return {
        success: true,
        data: {
          assinaturas: subscriptionsResult.data || [],
          cobrancas: (chargesResult.data || []).filter(item => subscriptionIds.has(item.preapproval_id))
        }
      };
    } catch (err) {
      console.error('Erro ao buscar assinaturas recorrentes:', err);
      return { success: false, error: err.message, data: { assinaturas: [], cobrancas: [] } };
    }
  }

  /**
   * Verifica se o plano está expirado e atualiza se necessário
   */
  static async verificarExpiracao(userId) {
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || authData.user?.id !== userId) throw new Error('Usuário inválido.');
      const { data, error } = await supabase.rpc('sincronizar_expiracao_acesso_usuario');
      if (error) throw error;
      return { success: true, ...data };
    } catch (err) {
      console.error('Erro ao verificar expiração:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Calcula dias restantes do plano
   */
  static diasRestantes(dataExpiracao) {
    if (!dataExpiracao) return null;
    
    const agora = new Date();
    const expiracao = new Date(dataExpiracao);
    const diff = expiracao - agora;
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    
    return dias > 0 ? dias : 0;
  }

  /**
   * Formata informações de um plano para exibição
   */
  static formatarPlanoInfo(planData) {
    const nomes = {
      sensor_free: getPlanName('sensor_free'),
      starter: getPlanName('starter'),
      premium: getPlanName('premium'),
      pro: getPlanName('pro'),
      essential: getPlanName('essential'),
      professional: getPlanName('professional'),
      management: getPlanName('management'),
      enterprise: getPlanName('enterprise'),
    };

    const badges = {
      sensor_free: { texto: 'Gratuito Sensor', cor: PAYMENT_CATALOG.sensor_free.color },
      starter: { texto: 'Starter Trial', cor: '#159f80' },
      premium: { texto: 'Premium', cor: '#FFD700' },
      pro: { texto: 'Pro', cor: '#9C27B0' },
      essential: { texto: 'Essencial', cor: PAYMENT_CATALOG.essential.color },
      professional: { texto: 'Profissional', cor: PAYMENT_CATALOG.professional.color },
      management: { texto: 'Gestão', cor: PAYMENT_CATALOG.management.color },
      enterprise: { texto: 'Enterprise', cor: PAYMENT_CATALOG.enterprise.color }
    };

    return {
      nome: nomes[planData.plano] || 'Desconhecido',
      badge: badges[planData.plano] || badges.sensor_free,
      diasRestantes: this.diasRestantes(planData.plano_data_expiracao),
      isTrial: planData.plano_trial,
      isExpirado: planData.plano_data_expiracao ? new Date(planData.plano_data_expiracao) < new Date() : false
    };
  }
}

export default PlanService;
